import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolInstance, isInstance } from '@synergy/config';
import { PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import { Stack, styled } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { PoolPageHeader } from '../../../components/page-header/page-header';
import { articleSearchResultsCategorySelector, articleSearchResultsSelector } from '../../../store/articles-selectors';
import { useAppSelector } from '../../../../store/store';

const SearchInformation = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'baseline',
    color: theme.palette.light.main,
}));

const SearchBannerSearch = (): ReactElement => {
    const [searchParams] = useSearchParams();
    const selectedCategoryId = Number(searchParams.get(FilterParams.CategoryId));
    const { currentCategory } = useAppSelector(state =>
        articleSearchResultsCategorySelector(state)(selectedCategoryId)
    );
    const search = searchParams.get(FilterParams.Search);

    return (
        <SearchInformation>
            <Stack>
                <PoolTypography variant="h4" translationId="app.filter.searchResult" values={{ search }} />
                <PoolTypography variant="body1">
                    <FormattedMessage
                        id="app.filter.inCategory"
                        values={{
                            name: currentCategory?.name ?? <FormattedMessage id="app.filter.allCategories" />,
                        }}
                    />
                </PoolTypography>
            </Stack>
        </SearchInformation>
    );
};

const SearchBannerCategory = (): ReactElement => {
    const [searchParams] = useSearchParams();
    const selectedCategoryId = Number(searchParams.get(FilterParams.CategoryId));
    const { categoryCounts } = useAppSelector(articleSearchResultsSelector);
    const currentCategory = categoryCounts.find(category => category.id === selectedCategoryId);

    return (
        <PoolTypography variant="h4">
            {currentCategory?.name ?? <FormattedMessage id="app.filter.allCategories" />}
        </PoolTypography>
    );
};

const SearchBannerSpecialOffer = (): ReactElement => {
    return <PoolTypography variant="h4" translationId="app.filter.specialOffer" />;
};

const SearchBannerSpecialPrice = (): ReactElement => {
    return <PoolTypography variant="h4" translationId="app.filter.hasSpecialPrice" />;
};

const SearchBannerContent = (): ReactElement => {
    const [searchParams] = useSearchParams();
    const specialOffer = searchParams.get(FilterParams.SpecialOffer);
    const search = searchParams.get(FilterParams.Search);
    const hasSpecialPrice = searchParams.get(FilterParams.SpecialPrice);

    if (search) {
        return <SearchBannerSearch />;
    } else if (specialOffer) {
        return <SearchBannerSpecialOffer />;
    } else if (hasSpecialPrice) {
        return <SearchBannerSpecialPrice />;
    } else {
        return <SearchBannerCategory />;
    }
};

export const SearchBanner = (): ReactElement => {
    const imageUrl =
        'assets/images/searchpage/' + (isInstance(PoolInstance.CH) ? 'search-banner-ch.png' : 'search-banner-at.jpg');

    return (
        <PoolPageHeader variant="blue" imageUrl={imageUrl}>
            <SearchInformation>
                <SearchBannerContent />
            </SearchInformation>
        </PoolPageHeader>
    );
};
